import type { FileData } from "./file.store"

export interface ExternalKeyApplication {
  _id: string
  isDefault?: boolean
  description: string
  company: string
  category: string
  url?: string
  lightLogo?: string
  darkLogo?: string
}

export type ExternalKeyApplicationPayload = Omit<ExternalKeyApplication, "_id" | "lightLogo" | "darkLogo">

export const useExternalKeyApplicationStore = defineStore("external-key-applications", () => {
  /** @deprecated */
  const externalKeyApplications = ref<Array<ExternalKeyApplication>>([])

  const getExternalKeyApplications = async (queryParameters?: Record<string, unknown>): Promise<Array<ExternalKeyApplication>> => {
    return useCrudMethods("/external-key-applications", externalKeyApplications).readItems({ pagination: false, ...queryParameters })
  }

  const getExternalKeyApplicationsPaginated = async <ReturnType = ExternalKeyApplication>(
    queryParameters?: Record<string, unknown>,
  ): Promise<PaginateResult<ReturnType>> => {
    const query = getUrlSearchParams(queryParameters)
    const { data } = await useSamApi<PaginateResult<ReturnType>>(`/external-key-applications?${query}`)
    return data
  }

  const getExternalKeyApplicationById = async (id: string): Promise<ExternalKeyApplication> => {
    return useCrudMethods("/external-key-applications", externalKeyApplications).readItemById(id)
  }

  const createExternalKeyApplication = async (payload: ExternalKeyApplicationPayload): Promise<ExternalKeyApplication> => {
    return useCrudMethods("/external-key-applications", externalKeyApplications).createItem(payload)
  }

  const updateExternalKeyApplication = async (id: string, payload: Partial<ExternalKeyApplication>): Promise<ExternalKeyApplication> => {
    return useCrudMethods("/external-key-applications", externalKeyApplications).updateItem(id, payload)
  }

  const deleteExternalKeyApplication = async (id: string): Promise<ExternalKeyApplication> => {
    return useCrudMethods("/external-key-applications", externalKeyApplications).deleteItem(id)
  }

  const uploadLogo = async (id: string, theme: "light" | "dark", file: File): Promise<Array<FileData>> => {
    const formData = new FormData()
    formData.append("files", file)

    const { data } = await useSamApi<FileData>(
      `/external-key-applications/${id}/logo?theme=${theme}`,
      {
        method: "POST",
        body: formData,
      },
      { forceNoContentType: true },
    )
    return [data]
  }

  const getLogo = async (id: string, theme: "light" | "dark") => {
    const { data: imageBlob } = await useSamApi<Blob>(`/external-key-applications/${id}/logo?theme=${theme}`, {
      headers: { Accept: "*/*" },
    })
    return URL.createObjectURL(imageBlob)
  }

  return {
    externalKeyApplications,

    getExternalKeyApplications,
    getExternalKeyApplicationsPaginated,
    getExternalKeyApplicationById,
    createExternalKeyApplication,
    updateExternalKeyApplication,
    deleteExternalKeyApplication,
    uploadLogo,
    getLogo,
  }
})
